import { CloseOutlined } from "@mui/icons-material";
import { AppBar, Box, Card, Dialog, Divider, Grid, IconButton, Toolbar } from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { numberValidation } from "layouts/views/common/validation";
import { useEffect, useState } from "react";
import { Success, Error_ } from "ui-components/Alert";
import { LoadingDialog } from "ui-components/Alert";
import { InfoButton } from "ui-components/Button";
import { SelectFields } from "ui-components/Form";
import { StatusDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import { StripedCheckboxDataGrid } from "ui-components/data-grids/StripedCheckboxDataGrid";
import {
  availableColumns,
  pickslipColumns,
  previewColumns,
  selectedSkuColumns,
  skuColumns,
} from "./columns";
import {
  getPickslipAmendOptions,
  getPickslipAmendPickslip,
  getPickslipAmendDetails,
  previewPickslipAmendDetails,
  savePickslipAmendDetails,
} from "services/data.outbound.service";
import { Texts } from "ui-components/Form";

function PickslipAmend({ customerCode }) {
  const [pickslipOptions, setPickslipOptions] = useState([]);
  const [pickslipOption, setPickslipOption] = useState(null);
  const [waveSKU, setWaveSKU] = useState([]);
  const [selectedSKU, setSelectedSKU] = useState([]);
  const [selectedId, setSelectedId] = useState([]);
  const [open, setOpen] = useState(false);
  const [debouncedValue, setDebouncedValue] = useState("");
  const [catalogueSearch, setCatalogueSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedValue(catalogueSearch);
    }, 500);
    return () => clearTimeout(t);
  }, [catalogueSearch]);

  const { isFetching: isPickslipOptionsFetching } = useQuery({
    queryKey: ["pickslip-amend-options", { customerCode }],
    queryFn: () => getPickslipAmendOptions({ customerCode }),
    enabled: Boolean(customerCode),
    onSuccess: (data) =>
      setPickslipOptions(
        data.data.data.map((d) => ({ value: d.pickslipId, label: d.pickslipNumber }))
      ),
  });

  const { isFetching: isPickslipsFetching, refetch } = useQuery({
    queryKey: ["pickslip-amend-pickslip", { pickslipOption }],
    queryFn: () => getPickslipAmendPickslip({ pickslipId: pickslipOption.value }),
    enabled: Boolean(pickslipOption),
    onSuccess: (data) => setWaveSKU(data.data.data.map((x, index) => ({ ...x, id: index + 1 }))),
  });

  return (
    <>
      <Card sx={{ mt: 2, p: 2 }}>
        {(isPickslipOptionsFetching || isPickslipsFetching) && <LoadingDialog />}
        <Grid container spacing={2}>
          <Grid item xs={4}>
            <SelectFields
              options={pickslipOptions}
              field={{
                label: "Pickslip Number",
                value: pickslipOption?.label ?? null,
                name: "pickslipNumber",
                errors: {},
              }}
              onChange={(e, v) => {
                setPickslipOption(v);
                setSelectedId([]);
                setSelectedSKU([]);
                setWaveSKU([]);
              }}
            />
          </Grid>
        </Grid>

        <MDTypography color="info" variant="h5">
          Pickslip Details
        </MDTypography>
        <Grid container spacing={3} sx={{mb: 2 }}>
            <Grid item xs={4}>
              <Texts
                field={{
                  label: "Search SKU Code",
                  value: catalogueSearch,
                }}
                onChange={(e) => setCatalogueSearch(e.target.value)}
              />
            </Grid>
          </Grid>
        <StripedCheckboxDataGrid
          columns={skuColumns}
          rows={waveSKU}
          filterModel={{
              items: [],
              quickFilterValues: debouncedValue.split(" ").filter((x) => x !== ""),
            }}
          onRowSelectionModelChange={(id) => setSelectedId(id)}
          rowSelectionModel={selectedId}
        />
        <MDBox display="flex" justifyContent="center" alignItems="center" mt={2}>
          <InfoButton
            onClick={() => {
              setSelectedSKU(waveSKU.filter((x) => selectedId.includes(x.id)));
              setOpen(true);
            }}
            disabled={selectedId.length === 0}
          >
            Edit
          </InfoButton>
        </MDBox>
      </Card>
      <UpdateModal
        orderNumber={pickslipOption}
        selectedSKU={selectedSKU}
        open={open}
        setOpen={setOpen}
        refetch={refetch}
      />
    </>
  );
}

export default PickslipAmend;

const UpdateModal = ({ orderNumber = null, selectedSKU = [], open, setOpen, refetch }) => {
  const [skuCode, setSKUCode] = useState(null);
  const [totalAllocate, setTotalAllocate] = useState(0);
  const [locations, setLocations] = useState([]);
  const [preview, setPreview] = useState([]);
  const [alert, setAlert] = useState(null);
  const [saveAlert, setSaveAlert] = useState(null);
  const [pickslipDetails, setPickslipDetails] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState([]);
  const [selectedCurrentLocationId, setSelectedCurrentLocationId] = useState([]);
  const totalQuantityPicked = pickslipDetails.reduce((total, detail) => {
    total = detail.quantityPicked + total;
    return total;
  }, 0);
  const totalQuantityPicked2 = preview.reduce((total, detail) => {
    if (detail.status !== "C") {
      total = detail.quantityPicked + total;
    }
    return total;
  }, 0);

  useEffect(() => {
    setSKUCode(null);
    setLocations([]);
    setPreview([]);
    setPickslipDetails([]);
    setSelectedLocationId([]);
    setSelectedCurrentLocationId([]);
    setTotalAllocate(0);
    setAlert(null);
  }, [open]);

  useEffect(() => {
    if (open) {
      if (selectedSKU.length > 0) {
        setSKUCode(selectedSKU[0].skuCode);
      }
    }
  }, [open]);
  useEffect(() => {
    setPreview([]);
  }, [skuCode]);
  const onError = (data) => setAlert(<Error_ data={data} onChange={setAlert} />);
  const queryClient = useQueryClient();

  const { isFetching } = useQuery({
    queryKey: ["amend", "details", { skuCode }],
    queryFn: () => getPickslipAmendDetails({ pickslipId: orderNumber.value, skuCode }),
    enabled: Boolean(orderNumber) && Boolean(skuCode),
    onSuccess: (data) => {
      const { details, locations } = data.data.data;
      setPickslipDetails(details);
      setLocations(locations.map((x) => ({ id: x.palletNbr, ...x })));
    },
  });

  const saveMutation = useMutation(previewPickslipAmendDetails, {});
  const updateMutation = useMutation(savePickslipAmendDetails, {});

  useEffect(() => {
    console.log(pickslipDetails);
    setLocations(
      locations.map((x) => {
        if (pickslipDetails.includes((d) => d.palletNbr == x.palletNbr)) {
          console.log(1);
          return { ...x, status: "O" };
        }
        return x;
      })
    );
  }, [pickslipDetails]);
  return (
    <Dialog fullScreen open={open} sx={{ padding: 1 }}>
      <AppBar sx={{ position: "relative", background: "#f5f5f5" }}>
        <Toolbar>
          <IconButton
            onClick={() => {
              queryClient.invalidateQueries(["wave-sku"]);
              setOpen(false);
            }}
          >
            <CloseOutlined />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 2 }}>
        {alert}
        <MDTypography color="info" variant="h5">
          Selected SKU Code List
        </MDTypography>
        <StatusDataGrid
          columns={selectedSkuColumns}
          rows={selectedSKU}
          onRowClick={(x) => setSKUCode(x.row.skuCode)}
          disableRowSelectionOnClick={false}
        />

        <Grid container spacing={5}>
          <Grid item xs={6} mt={2}>
            {isFetching && <LoadingDialog />}
            <MDTypography color="info" variant="h5">
              Pickslips Details
            </MDTypography>
            <Box sx={{ "& .bold": { fontWeight: 1000, background: "mintcream" } }}>
              <StripedCheckboxDataGrid
                columns={pickslipColumns}
                rows={[
                  ...pickslipDetails,
                  {
                    id: "Total",
                    label: "Total",
                    quantityPicked: totalQuantityPicked,
                  },
                ]}
                processRowUpdate={(updatedRow, originalRow) => {
                  let qtyUpdated = numberValidation(updatedRow.quantityPicked);

                  const location = locations.find((x) => x.palletNbr === updatedRow.palletNbr);
                  if (qtyUpdated > location.remainingStockQuantity) {
                    qtyUpdated = location.remainingStockQuantity;
                  }
                  const details = pickslipDetails.map((x) => {
                    if (x.id === updatedRow.id) {
                      x.quantityPicked = qtyUpdated;
                      originalRow.quantityPicked = qtyUpdated;
                    }
                    return x;
                  });
                  setPickslipDetails(details);
                  return originalRow;
                }}
                onProcessRowUpdateError={(x) => {
                  console.log(x);
                }}
                onRowSelectionModelChange={(id) => {
                  setSelectedCurrentLocationId(id);
                }}
                getCellClassName={({ row, field }) => {
                  if (row.id === "Total") {
                    if (
                      field === "zoneID" ||
                      field === "totalQuantityAllocate" ||
                      field === "quantityAllocate" ||
                      field === "quantityPicked"
                    ) {
                      return "bold";
                    }
                  }
                  return "";
                }}
                isRowSelectable={(params) => !(params.row.id === "Total")}
                isCellEditable={(params) => !(params.row.id === "Total")}
              />
            </Box>

            <MDBox display="flex" justifyContent="flex-end" mt={2}>
              <MDButton
                variant="gradient"
                color="error"
                onClick={() => {
                  // remove pickslip details
                  const filtered = pickslipDetails.filter(
                    (x) => !selectedCurrentLocationId.includes(x.id)
                  );
                  setPickslipDetails(filtered);
                }}
                disabled={selectedCurrentLocationId.length === 0}
              >
                Remove Location
              </MDButton>
              <MDButton
                sx={{ ml: 1 }}
                variant="gradient"
                color="info"
                onClick={() => {
                  if (
                    totalQuantityPicked !==
                    selectedSKU.filter((x) => x.skuCode == skuCode)[0].quantityPicked
                  ) {
                    setAlert(
                      <Error_
                        content="Pickslip details quantity mismatches total quantity allocate"
                        onChange={setAlert}
                      />
                    );
                    return;
                  }
                  if (pickslipDetails.some((x) => 1 > x.quantityPicked)) {
                    setAlert(
                      <Error_ content="Pickslip details contains 0 quantity " onChange={setAlert} />
                    );
                    return;
                  }
                  setAlert(null);

                  setPreview([]);
                  saveMutation.mutate(
                    {
                      pickslipId: orderNumber.value,
                      skuCode,
                      totalQuantityPicked,
                      locationItems: pickslipDetails,
                    },
                    {
                      onSuccess: (data) => setPreview(data.data.data),
                      onError,
                    }
                  );
                }}
                disabled={pickslipDetails.length === 0}
              >
                Save And Preview
              </MDButton>
            </MDBox>
          </Grid>
          <Grid item xs={6} mt={2}>
            <MDTypography color="info" variant="h5">
              Available Location
            </MDTypography>
            <StatusDataGrid
              columns={availableColumns}
              rows={locations.map((x) => {
                if (pickslipDetails.some((d) => d.palletNbr === x.palletNbr)) {
                  return { ...x, status: "O" };
                }
                return x;
              })}
              onRowSelectionModelChange={(id) => setSelectedLocationId(id)}
              isRowSelectable={(params) =>
                !pickslipDetails.some((x) => x.palletNbr === params.row.palletNbr)
              }
              checkboxSelection={true}
              pageSizeOptions={[10, 20, 30, 40, 50]}
              initialState={{
              pagination: { paginationModel: { pageSize: 10 } },
            }}
            />
            <MDBox display="flex" justifyContent="flex-end" mt={2}>
              <MDButton
                variant="gradient"
                color="info"
                onClick={() => {
                  const data = locations.filter((x) => selectedLocationId.includes(x.id));
                  const details = data.map((x) => ({
                    ...x,
                    totalQuantityAllocate: 0,
                    quantityAllocate: 0,
                    quantityPicked: 0,
                  }));
                  setPickslipDetails([...pickslipDetails, ...details]);
                }}
                disabled={selectedLocationId.length === 0}
              >
                Move Location
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>

        <Divider textAlign="left" sx={{ background: "#9ebeeb", opacity: "unset" }} />

        {saveAlert}

        <Box sx={{ "& .bold": { fontWeight: 1000, background: "mintcream" } }}>
          {isFetching && <LoadingDialog />}
          <MDTypography color="info" variant="h5">
            Preview Details
          </MDTypography>
          <StatusDataGrid
            columns={previewColumns}
            rows={
              preview.length > 0
                ? [
                    ...preview,
                    {
                      id: "Total",
                      label: "Total",
                      quantityAllocate: totalAllocate,
                      quantityPicked: totalQuantityPicked2,
                    },
                  ]
                : []
            }
            processRowUpdate={(updatedRow, originalRow) => {
              let qtyUpdated = numberValidation(updatedRow.quantityPicked);
              if (qtyUpdated > updatedRow.quantityAllocate) {
                qtyUpdated = updatedRow.quantityAllocate;
              }
              const details = preview.map((x) => {
                if (x.id === updatedRow.id) {
                  x.quantityPicked = qtyUpdated;
                  originalRow.quantityPicked = qtyUpdated;
                }
                return x;
              });
              setPreview(details);
              return originalRow;
            }}
            onProcessRowUpdateError={(x) => {
              console.log(x);
            }}
            getCellClassName={({ row, field }) => {
              if (row.id === "Total") {
                if (
                  field === "orderNumber" ||
                  field === "quantityAllocate" ||
                  field === "quantityPicked"
                ) {
                  return "bold";
                }
              }
              return "";
            }}
          />
        </Box>

        <MDBox display="flex" justifyContent="flex-end" mt={2}>
          <MDButton
            variant="gradient"
            color="info"
            onClick={() => {
              updateMutation.mutate(
                {
                  pickslipId: orderNumber.value,
                  skuCode,
                  details: preview,
                },
                {
                  onSuccess: (data) => {
                    setPreview([]);
                    queryClient.invalidateQueries(["amend"]);
                    setSaveAlert(<Success data={data} onChange={setSaveAlert} />);
                  },

                  onError,
                }
              );
            }}
            disabled={preview.length === 0}
          >
            Submit
          </MDButton>
        </MDBox>
      </Box>
    </Dialog>
  );
};
